import { useEffect, useState } from "react";
import { getProfile, deleteProfile } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import countriesData from "../data/countries";
import {
  MapPin, CalendarDays, Users, HeartPulse,
  MessageCircle, PenLine, Accessibility, Hand,
  Eye, Ear, Brain, Compass, Focus,
  UserRoundPen, LockKeyhole, Trash2, LogOut, X
} from "lucide-react";

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getProfile();
        setProfile(res.data);
      } catch (err) {
        console.error("Error loading profile", err);
      }
    };
    fetchProfile();
  }, []);

  const getCountryLabel = (code) => {
    const found = countriesData.find((c) => c.countryCode === code);
    return found?.countryLabel || code;
  };

  const getCityLabel = (code, countryCode) => {
    const country = countriesData.find((c) => c.countryCode === countryCode);
    return country?.cityCodes.find(
      (city) => city.toLowerCase() === (code || "").toLowerCase()
    ) || code;
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("profile_edit_page.not_defined", "Non défini");
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteProfile();
      localStorage.clear();
      alert(t("profile_edit_page.deleted_success", "Compte supprimé avec succès."));
      navigate("/login");
    } catch (err) {
      alert(t("profile_edit_page.delete_error", "Erreur lors de la suppression du compte."));
    }
  };

  if (!profile) {
    return (
      <div className="mx-auto mt-12 text-center text-[#245580]">
        <p className="animate-pulse">
          {t("profile_edit_page.loading", "Chargement du profil...")}
        </p>
      </div>
    );
  }

  const fields = [
    {
      icon: CalendarDays,
      label: t("common.birth_date", "Date de naissance"),
      value: formatDate(profile.date_of_birth)
    },
    {
      icon: Users,
      label: t("search_profile.community", "Communauté"),
      value: profile.comunidad
    },
    {
      icon: HeartPulse,
      label: t("profile.disease", "Maladie"),
      value: profile.enfermedad
    },
    { emoji: "🗣️", label: t("profile.habla"), value: profile.habla },
    { emoji: "✍️", label: t("profile.escribe"), value: profile.escribe },
    { emoji: "🚶", label: t("profile.movilidad"), value: profile.movilidad },
    { emoji: "🤲", label: t("profile.motricidad"), value: profile.motricidad },
    { emoji: "👁️", label: t("profile.ver"), value: profile.ver },
    { emoji: "👂", label: t("profile.audition"), value: profile.audition },
    { emoji: "🧠", label: t("profile.memoire"), value: profile.memoire },
    { emoji: "🧭", label: t("profile.orientation"), value: profile.orientation },
    { emoji: "🧩", label: t("profile.concentration"), value: profile.concentration }
  ];

  return (
    <main className="min-h-screen bg-[#DCECF9] px-3 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-4xl overflow-hidden rounded-3xl bg-white shadow-xl">

        {/* Cabecera */}
        <div className="h-28 bg-gradient-to-br from-[#F97316] via-[#FDBA74] to-[#FFEDD5] sm:h-36" />

        <div className="px-5 pb-8 sm:px-10">
          <div className="-mt-14 flex flex-col items-center text-center">
            <img
              src={profile.photo || "/default-avatar.png"}
              alt={t("profile.photo_alt", "Photo de profil")}
              className="h-28 w-28 rounded-full border-4 border-white bg-white object-cover shadow-lg sm:h-32 sm:w-32"
            />

            <h1 className="mt-4 text-2xl font-bold text-[#245580] sm:text-3xl">
              {profile.nombre} {profile.apellidos}
            </h1>

            {(profile.country || profile.ciudad) && (
              <div className="mt-2 flex items-center justify-center gap-1.5 text-sm text-slate-500">
                {(() => {
                  const flags = {
                    es: "🇪🇸",
                    fr: "🇫🇷",
                    de: "🇩🇪",
                    en: "🇬🇧",
                    it: "🇮🇹",
                    nl: "🇳🇱",
                    pl: "🇵🇱",
                    pt: "🇵🇹"
                  };

                  const country = (profile.country || "")
                    .trim()
                    .toLowerCase();

                  return flags[country] ? (
                    <span
                      role="img"
                      aria-label={getCountryLabel(profile.country)}
                      className="text-lg leading-none"
                    >
                      {flags[country]}
                    </span>
                  ) : (
                    <MapPin size={16} />
                  );
                })()}
                <span>
                  {[getCityLabel(profile.ciudad, profile.country),
                    getCountryLabel(profile.country)]
                    .filter(Boolean).join(", ")}
                </span>
              </div>
            )}

            {profile.bio && (
              <p className="mt-4 max-w-xl whitespace-pre-line text-base leading-relaxed text-slate-600">
                {profile.bio}
              </p>
            )}
          </div>

          {/* Información - HAPPYCAP_MY_PROFILE_COLOR_ICONS_V1 */}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(({ icon: Icon, emoji, label, value }, index) => (
              <div
                key={index}
                className="group flex min-w-0 items-start gap-3 rounded-[20px] border border-[#E3EDF4] bg-[#F7FAFD] p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#C9DFED] hover:bg-[#F1F8FC] hover:shadow-sm sm:p-5"
              >
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E1EDF5] bg-white text-xl shadow-sm"
                  aria-hidden="true"
                >
                  {emoji ? (
                    emoji
                  ) : Icon ? (
                    <Icon size={21} className="text-[#247BA8]" />
                  ) : null}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#648098]">
                    {label}
                  </p>

                  <p className="mt-1.5 break-words text-[15px] font-semibold leading-6 text-[#245580]">
                    {value || "—"}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Acciones */}
          <div className="mt-8 flex flex-col gap-3 border-t border-slate-100 pt-7 sm:flex-row sm:flex-wrap sm:justify-center">
            <button
              onClick={() => navigate("/edit-profile")}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F97316] px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-[#EA580C]"
            >
              <UserRoundPen size={19} />
              {t("profile_edit_page.edit_profile", "Modifier le profil")}
            </button>

            <button
              onClick={() => navigate("/change-password")}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#247BA8] bg-white px-5 py-3 font-semibold text-[#247BA8] transition hover:bg-[#F0F8FF]"
            >
              <LockKeyhole size={19} />
              {t("profile_edit_page.change_password", "Changer le mot de passe")}
            </button>

            <button
              onClick={() => {
                setConfirmDelete(false);
                setShowDeleteModal(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-5 py-3 font-semibold text-red-600 transition hover:bg-red-50"
            >
              <Trash2 size={19} />
              {t("profile_edit_page.delete_account", "Supprimer le compte")}
            </button>

            <button
              type="button"
              onClick={() => navigate("/logout")}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
            >
              <LogOut size={19} />
              {t("navbar.logout", "Se déconnecter")}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 px-4">
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-md rounded-3xl bg-white p-6 text-center shadow-2xl sm:p-8"
          >
            <button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              aria-label={t("common.close")}
              className="absolute right-4 top-4 rounded-full p-2 text-slate-500 hover:bg-slate-100"
            >
              <X size={20} />
            </button>

            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">
              <Trash2 size={26} />
            </div>

            <h2 className="mb-3 text-xl font-bold text-red-600">
              {t("profile_edit_page.confirm_delete_title", "Confirmer la suppression ?")}
            </h2>

            <p className="mb-5 text-sm leading-relaxed text-slate-600">
              {t("profile_edit_page.confirm_delete_text", "Cette action est irréversible. Êtes-vous sûr ?")}
            </p>

            <label className="mb-6 flex cursor-pointer items-start gap-3 rounded-xl bg-slate-50 p-3 text-left text-sm text-slate-700">
              <input
                type="checkbox"
                checked={confirmDelete}
                onChange={(e) => setConfirmDelete(e.target.checked)}
                className="mt-1 h-4 w-4 accent-red-600"
              />
              <span>
                {t("profile_edit_page.confirm_delete_checkbox", "Je comprends que je perdrai toutes mes données.")}
              </span>
            </label>

            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 rounded-xl bg-slate-100 px-4 py-3 font-semibold text-slate-700 hover:bg-slate-200"
              >
                {t("profile_edit_page.cancel", "Annuler")}
              </button>

              <button
                onClick={handleDeleteAccount}
                disabled={!confirmDelete}
                className="flex-1 rounded-xl bg-red-600 px-4 py-3 font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("profile_edit_page.confirm", "Oui, supprimer")}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProfilePage;
